<template>
  <div class="cancel-go-statement-component">
    <div>
      <form @submit.prevent>
        <div class="row">
          <!-- Beneficiary Details -->
          <div class="col-md-6">
            <h5 v-if="showSectionTitles">
              {{ $t("go_cancel.form.beneficiary_details_title") }}
            </h5>

            <div class="mb-3">
              <label
                :for="`beneficiary_name_${statementIndex}`"
                class="form-label"
              >
                {{ $t("go_cancel.form.beneficiary_name.label") }}
                <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': v$.localBeneficiaryName.$error }"
                :id="`beneficiary_name_${statementIndex}`"
                v-model="localBeneficiaryName"
                :placeholder="$t('go_cancel.form.beneficiary_name.placeholder')"
                required
                @blur="v$.localBeneficiaryName.$touch"
              />
              <div
                v-for="error of v$.localBeneficiaryName.$errors"
                :key="error.$uid"
                class="invalid-feedback"
              >
                {{ error.$message }}
              </div>
            </div>

            <div class="mb-3">
              <label
                :for="`registry_code_${statementIndex}`"
                class="form-label"
              >
                {{ $t("go_cancel.form.registry_code.label") }}
                <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': v$.localRegistryCode.$error }"
                :id="`registry_code_${statementIndex}`"
                v-model="localRegistryCode"
                :placeholder="$t('go_cancel.form.registry_code.placeholder')"
                required
                @blur="v$.localRegistryCode.$touch"
                @input="emitUpdate"
              />
              <div
                v-for="error of v$.localRegistryCode.$errors"
                :key="error.$uid"
                class="invalid-feedback"
              >
                {{ error.$message }}
              </div>
            </div>

            <div class="mb-3" v-if="!props.omitVolumePeriodRegistry">
              <label
                :for="`mwh_cancelled_${statementIndex}`"
                class="form-label"
              >
                {{ $t("go_cancel.form.mwh_cancelled.label") }}
                <span class="text-danger">*</span>
              </label>
              <input
                type="number"
                class="form-control"
                :class="{ 'is-invalid': v$.localMwhCancelled.$error }"
                :id="`mwh_cancelled_${statementIndex}`"
                v-model="localMwhCancelled"
                min="1"
                step="1"
                :placeholder="$t('go_cancel.form.mwh_cancelled.placeholder')"
                required
                @blur="v$.localMwhCancelled.$touch"
                @input="emitUpdate"
              />
              <div
                v-for="error of v$.localMwhCancelled.$errors"
                :key="error.$uid"
                class="invalid-feedback"
              >
                {{ error.$message }}
              </div>
            </div>

            <div class="mb-3">
              <label
                :for="`beneficiary_type_${statementIndex}`"
                class="form-label"
              >
                {{ $t("go_cancel.form.beneficiary_type.label") }}
                <span class="text-danger">*</span>
              </label>
              <select
                class="form-select"
                :class="{ 'is-invalid': v$.localBeneficiaryType.$error }"
                :id="`beneficiary_type_${statementIndex}`"
                v-model="localBeneficiaryType"
                required
                @blur="v$.localBeneficiaryType.$touch"
              >
                <option value="">{{ $t("common.select_option") }}</option>
                <!-- DEV-309: Filter to only show CONSUMER and ELECTRICITY_SELLER -->
                <option
                  v-for="item in allowedBeneficiaryTypes"
                  :key="item"
                  :value="item"
                >
                  {{ $t(`go_cancel.form.beneficiary_type.${item}`) }}
                </option>
              </select>
              <div
                v-for="error of v$.localBeneficiaryType.$errors"
                :key="error.$uid"
                class="invalid-feedback"
              >
                {{ error.$message }}
              </div>
            </div>

            <!-- DEV-309: Replace residency with registry selection -->
            <div class="mb-3" v-if="!props.omitVolumePeriodRegistry">
              <label
                :for="`registry_country_region_${statementIndex}`"
                class="form-label"
              >
                {{ $t("go_cancel.form.registry_country_region.label") }}
                <span class="text-danger">*</span>
              </label>
              <select
                class="form-select"
                :class="{ 'is-invalid': v$.localRegistryCountryRegion.$error }"
                :id="`registry_country_region_${statementIndex}`"
                v-model="localRegistryCountryRegion"
                required
                @blur="v$.localRegistryCountryRegion.$touch"
                @change="emitUpdate"
              >
                <option disabled value="">
                  {{ $t("go_cancel.form.registry_country_region.placeholder") }}
                </option>
                <option
                  v-for="registry in registries"
                  :key="registry.value"
                  :value="registry.value"
                >
                  {{ registry.label }}
                </option>
              </select>
              <div
                v-for="error of v$.localRegistryCountryRegion.$errors"
                :key="error.$uid"
                class="invalid-feedback"
              >
                {{ error.$message }}
              </div>
            </div>

            <!-- DEV-309: Address with dynamic requirement based on registry -->
            <div class="mb-3">
              <label :for="`address_${statementIndex}`" class="form-label">
                {{ $t("go_cancel.form.address.label") }}
                <span v-if="isAddressRequired" class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': v$.localAddress.$error }"
                :id="`address_${statementIndex}`"
                v-model="localAddress"
                :placeholder="$t('go_cancel.form.address.placeholder')"
                :required="isAddressRequired"
                @blur="v$.localAddress.$touch"
                @input="emitUpdate"
              />
              <div
                v-for="error of v$.localAddress.$errors"
                :key="error.$uid"
                class="invalid-feedback"
              >
                {{ error.$message }}
              </div>
              <div v-if="isAddressRequired" class="form-text">
                {{ $t("go_cancel.form.address.required_help") }}
              </div>
            </div>
          </div>

          <!-- Cancellation Details -->
          <div class="col-md-6">
            <h5 v-if="showSectionTitles">
              {{ $t("go_cancel.form.cancellation_details_title") }}
            </h5>

            <div class="mb-3" v-if="!props.omitVolumePeriodRegistry">
              <label :for="`period_start_${statementIndex}`" class="form-label">
                {{ $t("go_cancel.form.period_start.label") }}
                <span class="text-danger">*</span>
              </label>
              <input
                type="date"
                class="form-control"
                :class="{ 'is-invalid': v$.localPeriodStart.$error }"
                :id="`period_start_${statementIndex}`"
                v-model="localPeriodStart"
                required
                @blur="v$.localPeriodStart.$touch"
                @input="emitUpdate"
              />
              <div
                v-for="error of v$.localPeriodStart.$errors"
                :key="error.$uid"
                class="invalid-feedback"
              >
                {{ error.$message }}
              </div>
            </div>

            <div class="mb-3" v-if="!props.omitVolumePeriodRegistry">
              <label :for="`period_end_${statementIndex}`" class="form-label">
                {{ $t("go_cancel.form.period_end.label") }}
                <span class="text-danger">*</span>
              </label>
              <input
                type="date"
                class="form-control"
                :class="{
                  'is-invalid':
                    v$.localPeriodEnd.$error ||
                    (localPeriodEnd &&
                      localPeriodStart &&
                      new Date(localPeriodEnd) < new Date(localPeriodStart)),
                }"
                :id="`period_end_${statementIndex}`"
                v-model="localPeriodEnd"
                :min="localPeriodStart"
                required
                @blur="v$.localPeriodEnd.$touch"
                @input="emitUpdate"
              />
              <div
                v-if="
                  v$.localPeriodEnd.$error ||
                  (localPeriodEnd &&
                    localPeriodStart &&
                    new Date(localPeriodEnd) < new Date(localPeriodStart))
                "
                class="invalid-feedback"
              >
                <span v-if="v$.localPeriodEnd.$errors.length > 0">
                  {{ v$.localPeriodEnd.$errors[0].$message }}
                </span>
                <span v-else>
                  {{ $t("go_cancel.form.period_end.invalid_date_range") }}
                </span>
              </div>
            </div>

            <!-- DEV-309: Replace purpose/category dropdowns with single free-text purpose field -->
            <div class="mb-3">
              <label
                :for="`cancellation_purpose_${statementIndex}`"
                class="form-label"
              >
                {{ $t("go_cancel.form.cancellation_purpose_text.label") }}
                <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': v$.localCancellationPurpose.$error }"
                :id="`cancellation_purpose_${statementIndex}`"
                v-model="localCancellationPurpose"
                :placeholder="
                  $t('go_cancel.form.cancellation_purpose_text.placeholder')
                "
                required
                @blur="v$.localCancellationPurpose.$touch"
                @input="emitUpdate"
              />
              <div
                v-for="error of v$.localCancellationPurpose.$errors"
                :key="error.$uid"
                class="invalid-feedback"
              >
                {{ error.$message }}
              </div>
              <div class="form-text">
                {{ $t("go_cancel.form.cancellation_purpose_text.help") }}
              </div>
            </div>

            <!-- DEV-583: opt-in per caller; no region gating here because a PPA's registry only
                 resolves at execution time (non-Grexel registries ignore the stored flag). -->
            <div v-if="showPrivacyToggle" class="mb-3">
              <div class="form-check form-switch">
                <input
                  :id="`keep_private_${statementIndex}`"
                  v-model="localKeepPrivate"
                  class="form-check-input"
                  type="checkbox"
                  role="switch"
                  @change="emitUpdate"
                />
                <label
                  class="form-check-label"
                  :for="`keep_private_${statementIndex}`"
                >
                  {{ $t("go_cancel.form.keep_private.label") }}
                </label>
              </div>
              <div class="form-text">
                {{ $t("go_cancel.form.keep_private.ppa_hint") }}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import useVuelidate from "@vuelidate/core";
import { useI18n } from "vue-i18n";

import { computed, onMounted, ref, watch } from "vue";

import type { IndividualCancelStatement } from "@/types";
import { BeneficiaryTypeEnum } from "@/types";

import { useValidators } from "@/composables/useValidators";
import { SUPPORTED_REGISTRIES_WITH_LABELS } from "@/constants";
import { getRegistryRule } from "@/dashboard/inventory_management/common/cancellationRegistryRules";
import {
  getDefaultOperationNotesTemplate,
  isStatementPrivate,
  materializeOperationNotesTemplate,
  withStatementPrivacy,
} from "@/dashboard/inventory_management/common/cancelStatementHelpers";
import { getRegistryLabel } from "@/utils/registries";

interface Props {
  statement: IndividualCancelStatement;
  statementIndex: number;
  showSectionTitles?: boolean;
  omitVolumePeriodRegistry?: boolean;
  /** Show the "keep cancellation statement private" switch (DEV-583). Off for the legacy callers. */
  showPrivacyToggle?: boolean;
}

interface Emits {
  "update:statement": [statement: IndividualCancelStatement];
}

const props = withDefaults(defineProps<Props>(), {
  showSectionTitles: true,
  omitVolumePeriodRegistry: false,
  showPrivacyToggle: false,
});

const emit = defineEmits<Emits>();

const { t } = useI18n();
const { required, integer, requiredMin } = useValidators(t);

const registries = computed(() =>
  SUPPORTED_REGISTRIES_WITH_LABELS.map((r) => ({
    value: r.value,
    label: getRegistryLabel(r.value, t),
  }))
);

const allowedBeneficiaryTypes = [
  BeneficiaryTypeEnum.CONSUMER,
  BeneficiaryTypeEnum.ELECTRICITY_SELLER,
];

// Local reactive state for validation (following RequestInvoiceComponent.vue pattern)
const localBeneficiaryName = ref(props.statement.beneficiary_name);
const localBeneficiaryType = ref(props.statement.beneficiary_type);
const localRegistryCode = ref(props.statement.registry_code);
const localRegistryCountryRegion = ref(props.statement.registry_country_region);
const localAddress = ref(props.statement.address);
const localPeriodStart = ref(props.statement.period_start);
const localPeriodEnd = ref(props.statement.period_end);
const localMwhCancelled = ref(
  props.omitVolumePeriodRegistry ? null : props.statement.mwh_cancelled
);
const localCancellationPurpose = ref(
  props.statement.cancellation_purpose ?? ""
);
const localKeepPrivate = ref(
  isStatementPrivate(props.statement.grexel_options)
);
// Remembers the value we last auto-generated, so we only refresh the purpose while it
// still matches that default (or is blank) and never overwrite user-entered text.
const lastAutoPurpose = ref("");

const registryRule = computed(() =>
  localRegistryCountryRegion.value
    ? getRegistryRule(localRegistryCountryRegion.value)
    : { requiresAddress: false }
);

const isAddressRequired = computed(() => registryRule.value.requiresAddress);

const computeDefaultCancellationPurpose = (
  beneficiaryName?: string | null,
  beneficiaryType?: BeneficiaryTypeEnum | null
): string => {
  if (!beneficiaryName?.trim()) {
    return "";
  }
  return materializeOperationNotesTemplate(
    getDefaultOperationNotesTemplate(
      beneficiaryType ?? BeneficiaryTypeEnum.CONSUMER
    ),
    beneficiaryName
  );
};

const updateCancellationPurposeDefault = (): boolean => {
  const defaultText = computeDefaultCancellationPurpose(
    localBeneficiaryName.value,
    localBeneficiaryType.value
  );
  if (!defaultText) return false;
  const current = localCancellationPurpose.value ?? "";
  // Only auto-fill when the user hasn't supplied their own text: the field is
  // blank, or still holds the value we previously auto-generated.
  const isUserEdited =
    current.trim() !== "" && current !== lastAutoPurpose.value;
  if (isUserEdited) return false;
  lastAutoPurpose.value = defaultText;
  if (current !== defaultText) {
    localCancellationPurpose.value = defaultText;
    return true;
  }
  return false;
};

const validationRules = computed(() => ({
  localBeneficiaryName: { required },
  localBeneficiaryType: { required },
  localRegistryCode: { required },
  localRegistryCountryRegion: props.omitVolumePeriodRegistry
    ? {}
    : { required },
  localAddress: {
    required: computed(() => (isAddressRequired.value ? required : () => true)),
  },
  localPeriodStart: props.omitVolumePeriodRegistry ? {} : { required },
  // No March 31 prior-year cutoff here. The statement upload modal and the ecolabel statement
  // flows record cancellations that already happened at the registry, and the PPA counterparty
  // form omits the period fields entirely (the PPA scheduler derives the period). The cutoff
  // applies when a user places a new cancel order — CancelStatementsEditor and the backend's
  // validate_prior_year_cutoff enforce it there.
  localPeriodEnd: props.omitVolumePeriodRegistry ? {} : { required },
  localMwhCancelled: props.omitVolumePeriodRegistry
    ? {}
    : {
        required,
        integer,
        min: requiredMin(1),
      },
  localCancellationPurpose: { required },
}));

const v$ = useVuelidate(validationRules, {
  localBeneficiaryName,
  localBeneficiaryType,
  localRegistryCode,
  localRegistryCountryRegion,
  localAddress,
  localPeriodStart,
  localPeriodEnd,
  localMwhCancelled,
  localCancellationPurpose,
});

watch(
  () => props.statement,
  (newStatement) => {
    localBeneficiaryName.value = newStatement.beneficiary_name;
    localBeneficiaryType.value = newStatement.beneficiary_type;
    localRegistryCode.value = newStatement.registry_code;
    localRegistryCountryRegion.value = newStatement.registry_country_region;
    localAddress.value = newStatement.address;
    localPeriodStart.value = newStatement.period_start;
    localPeriodEnd.value = newStatement.period_end;
    localMwhCancelled.value = props.omitVolumePeriodRegistry
      ? null
      : (newStatement.mwh_cancelled ?? null);
    localCancellationPurpose.value = newStatement.cancellation_purpose ?? "";
    localKeepPrivate.value = isStatementPrivate(newStatement.grexel_options);
  },
  { deep: true, immediate: true }
);
// Refresh the autogenerated purpose when beneficiary name/type changes, but keep any
// custom text the user has typed (see updateCancellationPurposeDefault).
watch([localBeneficiaryName, localBeneficiaryType], () => {
  if (updateCancellationPurposeDefault()) {
    emitUpdate();
  }
});
// Parent provides defaults; no need to initialize period here

function emitUpdate(): void {
  const updatedStatement: IndividualCancelStatement = {
    ...props.statement,
    beneficiary_name: localBeneficiaryName.value,
    beneficiary_type: localBeneficiaryType.value,
    registry_code: localRegistryCode.value,
    registry_country_region: props.omitVolumePeriodRegistry
      ? undefined
      : (localRegistryCountryRegion.value ?? undefined),
    address: localAddress.value,
    period_start: props.omitVolumePeriodRegistry
      ? undefined
      : (localPeriodStart.value ?? undefined),
    period_end: props.omitVolumePeriodRegistry
      ? undefined
      : (localPeriodEnd.value ?? undefined),
    cancellation_purpose: localCancellationPurpose.value,
    mwh_cancelled: props.omitVolumePeriodRegistry
      ? null
      : localMwhCancelled.value,
    // DEV-583: rebuilt from local state like every field above, so an untoggle clears a stored
    // value instead of the spread carrying it through.
    grexel_options: withStatementPrivacy(localKeepPrivate.value),
  };
  emit("update:statement", updatedStatement);
}

onMounted(() => {
  if (props.omitVolumePeriodRegistry) {
    emitUpdate();
  }
});
</script>

<style scoped lang="scss">
.cancel-go-statement-component {
  .cursor-pointer {
    cursor: pointer;
  }
}
</style>
